import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, setToken } from "@/lib/api";

export function AuthForm({ mode }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const isSignUp = mode === "sign-up";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [institutionName, setInstitutionName] = useState("");
  const [institutionType, setInstitutionType] = useState("bank");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (isSignUp) {
        const { data } = await api.post("/auth/register", { name, email, password });
        setToken(data.token);
        if (data.user.role === "client" && institutionName) {
          try {
            await api.post("/me/profile", { institutionName, institutionType, contactPerson: name });
          } catch (_) {}
        }
      } else {
        const { data } = await api.post("/auth/login", { email, password });
        setToken(data.token);
      }
      navigate("/portal");
    } catch (err) {
      setError(err?.response?.data?.detail ?? "Etwas ist schiefgelaufen.");
      setLoading(false);
    }
  };

  return (
    <main className="flex min-h-screen flex-col bg-secondary/40">
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-4 py-5">
        <Link to="/"><BrandLogo /></Link>
        <LanguageSwitcher />
      </div>

      <div className="flex flex-1 items-center justify-center px-4 pb-16">
        <Card className="w-full max-w-md p-7">
          <div className="mb-6">
            <h1 className="font-serif text-2xl font-semibold tracking-tight text-foreground">
              {isSignUp ? t("auth.signup.title") : t("auth.signin.title")}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {isSignUp ? t("auth.signup.subtitle") : t("auth.signin.subtitle")}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {isSignUp && (
              <>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="institutionName">{t("auth.institutionName")}</Label>
                  <Input id="institutionName" value={institutionName} onChange={(e) => setInstitutionName(e.target.value)} />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="institutionType">{t("auth.institutionType")}</Label>
                  <Select value={institutionType} onValueChange={(v) => setInstitutionType(v ?? "bank")}>
                    <SelectTrigger id="institutionType"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank">{t("auth.type.bank")}</SelectItem>
                      <SelectItem value="asset">{t("auth.type.asset")}</SelectItem>
                      <SelectItem value="fund">{t("auth.type.fund")}</SelectItem>
                      <SelectItem value="other">{t("auth.type.other")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="name">{t("auth.name")}</Label>
                  <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" />
                </div>
              </>
            )}

            <div className="flex flex-col gap-2">
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="password">{t("auth.password")}</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoComplete={isSignUp ? "new-password" : "current-password"} />
            </div>

            {error && <p className="text-sm text-destructive" role="alert">{error}</p>}

            <Button type="submit" disabled={loading} className="mt-1 w-full">
              {loading ? t("auth.wait") : isSignUp ? t("auth.signup.button") : t("auth.signin.button")}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            <Link to={isSignUp ? "/sign-in" : "/sign-up"} className="font-medium text-foreground underline-offset-4 hover:underline">
              {isSignUp ? t("auth.toSignin") : t("auth.toSignup")}
            </Link>
          </p>
        </Card>
      </div>
    </main>
  );
}

export default AuthForm;
